import { useAuth } from '../hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { HardDrive, Shield, Bell, BarChart3 } from 'lucide-react';

export default function Login() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  const features = [
    {
      icon: HardDrive,
      title: 'Real-time Monitoring',
      description: 'Track disk usage across all your Linux servers in one place.',
    },
    {
      icon: BarChart3,
      title: 'Top Files & Folders',
      description: 'Instantly see what\'s consuming your disk space.',
    },
    {
      icon: Bell,
      title: 'Smart Alerts',
      description: 'Get notified when disk usage changes significantly.',
    },
    {
      icon: Shield,
      title: 'AI-Powered Insights',
      description: 'Understand why your disk space is changing.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950 flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-2xl backdrop-blur-xl">
              📊
            </div>
            <span className="text-2xl font-bold text-white">WatchTheSize</span>
          </div>
        </div>

        <div className="space-y-8">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Monitor disk usage across<br />
            <span className="bg-gradient-to-r from-primary-300 to-pink-300 bg-clip-text text-transparent">
              all your servers
            </span>
          </h1>
          <p className="text-lg text-primary-200 max-w-md">
            Stop running out of disk space. Get real-time alerts and AI-powered insights about what's consuming your storage.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="rounded-2xl bg-white/5 p-5 backdrop-blur-xl border border-white/10"
              >
                <Icon className="h-8 w-8 text-primary-300 mb-3" />
                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-primary-200/80">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-white p-8 shadow-2xl shadow-black/20">
            {/* Mobile logo */}
            <div className="flex items-center justify-center gap-3 mb-8 lg:hidden">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-2xl shadow-lg shadow-primary-500/30">
                📊
              </div>
              <span className="text-2xl font-bold text-neutral-900">WatchTheSize</span>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Welcome back</h2>
              <p className="text-neutral-500">Sign in to access your dashboard</p>
            </div>

            {error && (
              <div className="mb-6 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                {error === 'missing_code_or_state'
                  ? 'Authentication failed. Please try again.'
                  : error}
              </div>
            )}

            <button
              onClick={login}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-neutral-200 bg-white px-6 py-4 text-sm font-semibold text-neutral-700 shadow-sm transition-all hover:bg-neutral-50 hover:shadow-md"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <div className="mt-8 text-center text-xs text-neutral-400">
              By signing in, you agree to our{' '}
              <a href="#" className="text-primary-600 hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-primary-600 hover:underline">
                Privacy Policy
              </a>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-primary-200 lg:hidden">
            Monitor disk usage across all your Linux servers
          </div>
        </div>
      </div>
    </div>
  );
}

